// Global state store for newsnetwide.com frontend

import { reactive, computed } from './reactivity.js';

// ============================================================
// Reactive State
// ============================================================
export const state = reactive({
    // Globe state
    globe: {
        initialized: false,
        rotation: { x: 0, y: 0 },
        targetRotation: { x: 0, y: 0 },
        zoom: 1,
        targetZoom: 1,
        autoRotate: true,
        autoRotateSpeed: 0.0005,
    },

    // Data
    countries: [],
    // iso2 -> { count, breaking, categories:[{slug,count}] } for the current
    // time window, from /api/countries/stats. Replaced wholesale so watchers fire.
    countryStats: {},
    categories: [],
    news: {
        latest: [],
        breaking: [],
        byCountry: new Map(),
        byCategory: new Map(),
    },
    sponsorships: {
        active: [],
        byEntity: new Map(),
    },

    // UI State
    ui: {
        selectedCountry: null,
        selectedCategory: null,
        selectedContinent: null,
        hoveredCountry: null,
        panelOpen: false,
        loading: true,
        error: null,
        lastUpdated: null,
    },

    // Controls
    controls: {
        timeRange: '24h', // '24h', '7d', '30d'
        categoryFilter: 'all',
        continentFilter: 'all',
    },
});

// ============================================================
// Computed / Derived State
// ============================================================
export const getters = {
    // Countries filtered by continent
    filteredCountries: computed(() => {
        const { continentFilter } = state.controls;
        if (continentFilter === 'all') return state.countries;
        return state.countries.filter((c) => c.continent === continentFilter);
    }),

    // News for selected country
    countryNews: computed(() => {
        const { selectedCountry } = state.ui;
        if (!selectedCountry) return [];
        return state.news.byCountry.get(selectedCountry) || [];
    }),

    // News for selected category
    categoryNews: computed(() => {
        const { selectedCategory } = state.ui;
        if (!selectedCategory || selectedCategory === 'all') return state.news.latest;
        return state.news.byCategory.get(selectedCategory) || [];
    }),

    // Active sponsorships for current view
    visibleSponsorships: computed(() => {
        const { selectedCountry, selectedContinent } = state.ui;
        const sponsorships = [];

        if (selectedCountry) {
            sponsorships.push(
                ...(state.sponsorships.byEntity.get(`country:${selectedCountry}`) || []),
            );
        }
        if (selectedContinent) {
            sponsorships.push(
                ...(state.sponsorships.byEntity.get(`continent:${selectedContinent}`) || []),
            );
        }
        // Global sponsorships always shown
        sponsorships.push(...(state.sponsorships.byEntity.get('global:world') || []));

        return sponsorships;
    }),

    // Category color map for globe pins
    categoryColors: computed(() => {
        const map = {};
        for (const cat of state.categories) {
            map[cat.slug] = cat.color;
        }
        return map;
    }),
};

// ============================================================
// Actions (mutations)
// ============================================================
export const actions = {
    // Globe
    setGlobeInitialized(value) {
        state.globe.initialized = value;
    },
    setGlobeRotation(x, y) {
        state.globe.rotation.x = x;
        state.globe.rotation.y = y;
    },
    setGlobeTargetRotation(x, y) {
        state.globe.targetRotation.x = x;
        state.globe.targetRotation.y = y;
    },
    setGlobeZoom(zoom) {
        state.globe.zoom = zoom;
    },
    setGlobeTargetZoom(zoom) {
        state.globe.targetZoom = Math.max(0.5, Math.min(3, zoom));
    },
    toggleAutoRotate() {
        state.globe.autoRotate = !state.globe.autoRotate;
    },

    // Data
    setCountries(countries) {
        state.countries = countries;
    },
    setCountryStats(list) {
        const map = {};
        for (const s of list) map[s.iso2] = s;
        state.countryStats = map;
    },
    setCategories(categories) {
        state.categories = categories;
    },
    setLatestNews(news) {
        state.news.latest = news;
        state.ui.lastUpdated = new Date().toISOString();
    },
    setBreakingNews(news) {
        state.news.breaking = news;
    },
    setCountryNews(iso2, news) {
        state.news.byCountry.set(iso2.toUpperCase(), news);
    },
    setCategoryNews(slug, news) {
        state.news.byCategory.set(slug, news);
    },
    setActiveSponsorships(sponsorships) {
        state.sponsorships.active = sponsorships;
        // Rebuild byEntity map
        state.sponsorships.byEntity.clear();
        for (const sp of sponsorships) {
            const key = `${sp.entity_type}:${sp.entity_id}`;
            if (!state.sponsorships.byEntity.has(key)) {
                state.sponsorships.byEntity.set(key, []);
            }
            state.sponsorships.byEntity.get(key).push(sp);
        }
    },

    // UI
    selectCountry(iso2) {
        state.ui.selectedCountry = iso2 ? iso2.toUpperCase() : null;
        state.ui.panelOpen = !!iso2;
        state.ui.hoveredCountry = null;
    },
    hoverCountry(iso2) {
        if (!state.ui.selectedCountry) {
            state.ui.hoveredCountry = iso2 ? iso2.toUpperCase() : null;
        }
    },
    closePanel() {
        state.ui.selectedCountry = null;
        state.ui.panelOpen = false;
    },
    setLoading(loading) {
        state.ui.loading = loading;
    },
    setError(error) {
        state.ui.error = error;
    },
    clearError() {
        state.ui.error = null;
    },

    // Controls
    setTimeRange(range) {
        state.controls.timeRange = range;
    },
    setCategoryFilter(category) {
        state.controls.categoryFilter = category;
    },
    setContinentFilter(continent) {
        state.controls.continentFilter = continent;
    },
};

// ============================================================
// Initialize store
// ============================================================
export function initStore() {
    // Load persisted preferences
    try {
        const saved = localStorage.getItem('newsnetwide-prefs');
        if (saved) {
            const prefs = JSON.parse(saved);
            if (prefs.timeRange) state.controls.timeRange = prefs.timeRange;
            if (prefs.categoryFilter) state.controls.categoryFilter = prefs.categoryFilter;
            if (prefs.continentFilter) state.controls.continentFilter = prefs.continentFilter;
        }
    } catch (e) {
        // Ignore localStorage errors
    }

    // Persist preferences on change
    const originalSetTimeRange = actions.setTimeRange;
    actions.setTimeRange = function (range) {
        originalSetTimeRange(range);
        persistPrefs();
    };
    const originalSetCategoryFilter = actions.setCategoryFilter;
    actions.setCategoryFilter = function (category) {
        originalSetCategoryFilter(category);
        persistPrefs();
    };
    const originalSetContinentFilter = actions.setContinentFilter;
    actions.setContinentFilter = function (continent) {
        originalSetContinentFilter(continent);
        persistPrefs();
    };
}

function persistPrefs() {
    try {
        localStorage.setItem(
            'newsnetwide-prefs',
            JSON.stringify({
                timeRange: state.controls.timeRange,
                categoryFilter: state.controls.categoryFilter,
                continentFilter: state.controls.continentFilter,
            }),
        );
    } catch (e) {
        // Ignore
    }
}

export default { state, getters, actions, initStore };
